import { useEffect, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Activity, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { scenarioTypes } from './constants';

export const SECTION_COLORS = {
    candidate_screening: 'hsl(var(--chart-1))',
    client_intake: 'hsl(var(--chart-2))',
    objection_handling: 'hsl(var(--chart-4))',
    salary_negotiation: 'hsl(var(--chart-3))',
    job_analysis: '#8b5cf6',
    communication: '#ec4899'
};

const TREND_META = {
    improving: { label: 'Improving', icon: TrendingUp, className: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/30' },
    steady: { label: 'Steady', icon: Minus, className: 'text-amber-600 bg-amber-500/10 border-amber-500/30' },
    declining: { label: 'Declining', icon: TrendingDown, className: 'text-red-600 bg-red-500/10 border-red-500/30' }
};

const sectionTitle = (id) => scenarioTypes.find((s) => s.id === id)?.title || id;
const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');

export const StatTile = ({ label, value, testId }) => (
    <div className="rounded-lg border bg-background/60 px-3 py-2">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-xl font-semibold tabular-nums" data-testid={testId}>{value}</p>
    </div>
);

export const TrendBadge = ({ summary }) => {
    const meta = TREND_META[summary.trend];
    if (!meta) {
        return <p className="text-xs text-muted-foreground" data-testid="trend-badge">Complete one more session to see your trend</p>;
    }
    const Icon = meta.icon;
    return (
        <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${meta.className}`} data-testid="trend-badge">
            <Icon className="h-3.5 w-3.5" />
            {meta.label}
            <span className="font-normal opacity-80">{summary.delta > 0 ? '+' : ''}{summary.delta} pts vs earlier sessions</span>
        </div>
    );
};

const TrendTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
            <p className="font-semibold" style={{ color: SECTION_COLORS[p.scenario_type] }}>{sectionTitle(p.scenario_type)}</p>
            {p.label && <p className="text-muted-foreground">{p.label}</p>}
            <p className="mt-1"><span className="text-base font-bold tabular-nums">{p.score}</span> / 100 · {formatDate(p.date)}</p>
            <p className="text-muted-foreground">Hints used: {p.hints_used || 0}</p>
        </div>
    );
};

const renderDot = ({ cx, cy, payload, index }) => (
    <circle key={`dot-${index}`} cx={cx} cy={cy} r={4.5} fill={SECTION_COLORS[payload.scenario_type] || 'hsl(var(--primary))'} stroke="hsl(var(--background))" strokeWidth={2} />
);

const TrendPlot = ({ points, average }) => {
    const data = points.map((p, i) => ({ ...p, index: i + 1 }));
    const tickLabel = (i) => {
        const label = formatDate(data[i - 1]?.date);
        return i > 1 && label === formatDate(data[i - 2]?.date) ? '' : label;
    };
    return (
        <div className="h-[200px] w-full" data-testid="score-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 10, right: 44, left: -18, bottom: 0 }}>
                    <defs>
                        <linearGradient id="scoreTrendFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="index" tickLine={false} axisLine={false} fontSize={11} tickFormatter={tickLabel} interval={data.length <= 12 ? 0 : 'preserveStartEnd'} />
                    <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickLine={false} axisLine={false} fontSize={11} />
                    <ReferenceLine y={average} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4" label={{ value: `Avg ${average}`, position: 'right', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                    <Tooltip content={<TrendTooltip />} cursor={{ stroke: 'hsl(var(--border))' }} />
                    <Area type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2.5} fill="url(#scoreTrendFill)" dot={renderDot} activeDot={{ r: 6 }} isAnimationActive />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

const SectionLegend = ({ points }) => {
    const sections = [...new Set(points.map((p) => p.scenario_type))];
    return (
        <div className="flex flex-wrap gap-3 pt-1" data-testid="score-trend-legend">
            {sections.map((id) => (
                <span key={id} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="h-2 w-2 rounded-full" style={{ background: SECTION_COLORS[id] }} />
                    {sectionTitle(id)}
                </span>
            ))}
        </div>
    );
};

export const ScoreTrendChart = ({ api }) => {
    const [section, setSection] = useState('all');
    const [trend, setTrend] = useState(null);

    useEffect(() => {
        let active = true;
        setTrend(null);
        api.get('/simulation/score-trend', { params: { scenario_type: section } })
            .then(({ data }) => active && setTrend(data))
            .catch(() => active && setTrend({ points: [], summary: { count: 0 } }));
        return () => { active = false; };
    }, [api, section]);

    const summary = trend?.summary;
    return (
        <Card className="overflow-hidden" data-testid="score-trend-card">
            <CardContent className="p-5 space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="flex items-center gap-2 text-base md:text-lg font-semibold">
                            <Activity className="h-5 w-5 text-primary" /> Your Score Trend
                        </h2>
                        <p className="text-sm text-muted-foreground">Overall score of your last 30 completed simulations</p>
                    </div>
                    <Select value={section} onValueChange={setSection}>
                        <SelectTrigger className="w-full sm:w-[200px]" data-testid="score-trend-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all" data-testid="score-trend-filter-all">All sections</SelectItem>
                            {scenarioTypes.map((s) => (
                                <SelectItem key={s.id} value={s.id} data-testid={`score-trend-filter-${s.id}`}>{s.title}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {!trend ? (
                    <Skeleton className="h-[200px] w-full" />
                ) : summary.count === 0 ? (
                    <div className="flex h-[160px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed text-center" data-testid="score-trend-empty">
                        <Activity className="h-6 w-6 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground max-w-xs">
                            {section === 'all' ? 'Complete your first simulation' : `Complete a ${sectionTitle(section)} simulation`} to start tracking your progress.
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                                <StatTile label="Latest" value={summary.latest} testId="trend-latest" />
                                <StatTile label="Best" value={summary.best} testId="trend-best" />
                                <StatTile label="Average" value={summary.average} testId="trend-average" />
                                <StatTile label="Sessions" value={summary.count} testId="trend-count" />
                            </div>
                            <TrendBadge summary={summary} />
                        </div>
                        <div>
                            <TrendPlot points={trend.points} average={summary.average} />
                            {section === 'all' && <SectionLegend points={trend.points} />}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
